// client/src/pages/ExamCheck.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import logo from "../assets/logo.png"; // Ensure this path is correct

// Icons
import { 
  Camera, 
  Mic, 
  Wifi, 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft, 
  Shield, 
  PlayCircle,
  RefreshCw,
  Monitor
} from "lucide-react";

function ExamCheck() {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Audio visualizer refs
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  const [cameraOk, setCameraOk] = useState(false);
  const [micOk, setMicOk] = useState(false);
  const [networkOk, setNetworkOk] = useState(navigator.onLine);
  const [micLevel, setMicLevel] = useState(0); // 0–1
  const [loading, setLoading] = useState(true);
  const [examTitle, setExamTitle] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // agreements
  const [agreeIdentity, setAgreeIdentity] = useState(false);
  const [agreeRules, setAgreeRules] = useState(false);

  // --- LOGIC SECTION (KEPT EXACTLY AS PROVIDED) ---

  const quickAccessCheck = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch (err) {
      return false;
    }
  };

  const cleanupMedia = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (_) {}
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startMicVisualizer = (stream) => {
    try {
      const audioContext =
        audioContextRef.current ||
        new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const avg = sum / dataArray.length; // 0–255
        
        setMicLevel(avg / 255); 
        
        animationFrameRef.current = requestAnimationFrame(tick);
      };

      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = requestAnimationFrame(tick);
    } catch (e) {
      console.warn("Audio visualizer error:", e);
    }
  };

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await api.get("/api/attempts/my");
        const attempt = (res.data || []).find((a) => a._id === attemptId);
        setExamTitle(attempt?.examId?.title || "Certification Exam");
      } catch {
        setExamTitle("Certification Exam");
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [attemptId]);

  const runChecks = async () => {
    setErrorMsg("");
    setCameraOk(false);
    setMicOk(false);
    setMicLevel(0);
    setNetworkOk(navigator.onLine);

    cleanupMedia();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }

      setCameraOk(true);
      setMicOk(true);
      startMicVisualizer(stream);
    } catch (e) {
      console.warn("Media error:", e);
      setCameraOk(false);
      setMicOk(false);
      setErrorMsg(
        "We couldn't access your camera or microphone. Please allow access in your browser and try again."
      );
    }

    setNetworkOk(navigator.onLine);
  };

  useEffect(() => {
    runChecks();

    const handleOnline = () => setNetworkOk(true);
    const handleOffline = () => setNetworkOk(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      cleanupMedia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allGood = cameraOk && micOk && networkOk;
  const agreementsOk = agreeIdentity && agreeRules;
  const canStart = allGood && agreementsOk;

  const handleStartExam = async () => {
    setErrorMsg("");

    const ok = await quickAccessCheck();
    if (!ok) {
      setErrorMsg(
        "Camera or microphone is blocked. Please allow access and try again."
      );
      return;
    }

    if (!allGood) {
      setErrorMsg("All checks must be green before starting the exam.");
      return;
    }
    if (!agreementsOk) {
      setErrorMsg(
        "Please confirm that you are the candidate and that you agree to the exam rules."
      );
      return;
    }

    if (document.documentElement.requestFullscreen) {
      try {
        await document.documentElement.requestFullscreen();
      } catch (e) {
        console.warn("Fullscreen error:", e);
      }
    }

    cleanupMedia();

    navigate(`/exam-session/${attemptId}`, {
      state: { cameraOk, micOk, networkOk },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center font-sans">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
        <p className="text-gray-500">Preparing environment...</p>
      </div>
    );
  }

  // --- UI RENDER (App-like, No Scroll) ---
  return (
    <div className="h-screen w-screen bg-gray-50 overflow-hidden font-sans flex flex-col">
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 h-16 shrink-0 flex items-center justify-between px-6 z-10">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Orivon" className="h-8 w-auto" />
          <div className="hidden md:block w-px h-6 bg-gray-300 mx-1"></div>
          <p className="text-gray-500 text-sm font-medium">System Readiness Check</p>
        </div>
        <div className="flex items-center gap-2 bg-orange-50 px-3 py-1.5 rounded-full border border-orange-100">
           <Monitor size={16} className="text-orange-600" />
           <span className="text-sm font-semibold text-orange-800 truncate max-w-[200px]">{examTitle}</span>
        </div>
      </header>

      {/* Main Content Area - Split View */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden p-4 lg:p-6 gap-6 max-w-7xl mx-auto w-full">
        
        {/* LEFT PANEL: Rules (Scrollable internally if needed) */}
        <section className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden order-2 lg:order-1">
          <div className="p-6 border-b border-gray-100 shrink-0">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Shield className="text-gray-400" size={24} /> Exam Rules & Honor Code
            </h2>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
             <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
                Please read these carefully. Any violation can result in <strong>cancellation of your certification</strong>.
             </div>

             <ul className="space-y-4">
                {[
                  { title: "Identity Verification", desc: "You must be the registered candidate. Do not let others take the exam." },
                  { title: "Camera & Mic", desc: "Must remain ON. Covering the camera or leaving the view is a violation." },
                  { title: "No External Help", desc: "Phones, notes, and other devices are strictly prohibited." },
                  { title: "Browser Focus", desc: "Switching tabs or minimizing the window is monitored and recorded." },
                  { title: "One-Time Access", desc: "Once started, the timer cannot be paused or reset." },
                ].map((rule, idx) => (
                  <li key={idx} className="flex gap-3 text-sm text-gray-600">
                    <div className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></div>
                    <div>
                      <strong className="text-gray-900 block mb-0.5">{rule.title}</strong>
                      {rule.desc}
                    </div>
                  </li>
                ))}
             </ul>
          </div>

          <div className="p-6 border-t border-gray-100 bg-gray-50 shrink-0 space-y-3">
             <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-orange-300 cursor-pointer transition-colors">
                <input type="checkbox" checked={agreeIdentity} onChange={(e) => setAgreeIdentity(e.target.checked)} className="mt-1 w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <span className="text-sm text-gray-700">I confirm that I am the <strong>registered candidate</strong>.</span>
             </label>
             <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white hover:border-orange-300 cursor-pointer transition-colors">
                <input type="checkbox" checked={agreeRules} onChange={(e) => setAgreeRules(e.target.checked)} className="mt-1 w-4 h-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                <span className="text-sm text-gray-700">I agree to the <strong>exam rules</strong> and understand violations lead to ban.</span>
             </label>
          </div>
        </section>

        {/* RIGHT PANEL: System Check (Fixed Height) */}
        <section className="w-full lg:w-[420px] bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-200 flex flex-col order-1 lg:order-2 overflow-hidden shrink-0">
          <div className="p-6 border-b border-gray-100 bg-gray-50">
             <h2 className="text-lg font-bold text-gray-900">System Check</h2>
             <p className="text-xs text-gray-500 mt-1">Status must be green to proceed.</p>
          </div>

          <div className="p-6 flex-1 flex flex-col gap-5 overflow-y-auto">
             
             {/* Camera Feed */}
             <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                   <div className="flex items-center gap-2 text-gray-700 font-medium">
                      <Camera size={16} /> Camera
                   </div>
                   <span className={`text-xs px-2 py-0.5 rounded-full border ${cameraOk ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                      {cameraOk ? "Connected" : "Not Detected"}
                   </span>
                </div>
                <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden border border-gray-300 relative shadow-inner group">
                   <video ref={videoRef} className="w-full h-full object-cover transform scale-x-[-1]" autoPlay muted playsInline />
                   {!cameraOk && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                         <Camera size={32} className="mb-2 opacity-50"/>
                         <span className="text-xs">No Signal</span>
                      </div>
                   )}
                   <div className="absolute top-2 right-2">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                   </div>
                </div>
             </div>

             {/* Microphone Visualizer */}
             <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                   <div className="flex items-center gap-2 text-gray-700 font-medium">
                      <Mic size={16} /> Microphone
                   </div>
                   <span className={`text-xs px-2 py-0.5 rounded-full border ${micOk ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                      {micOk ? "Active" : "Muted"}
                   </span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                    <div 
                      className={`h-full transition-all duration-100 ease-out rounded-full ${micLevel > 0.6 ? 'bg-gradient-to-r from-green-500 to-green-400' : 'bg-indigo-500'}`}
                      style={{ width: `${Math.max(micLevel, 0.05) * 100}%` }}
                    />
                </div>
                <p className="text-xs text-gray-400">Please say something to test.</p>
             </div>

             {/* Internet Status */}


             {/* Error Box */}
             {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-xs text-red-700">
                   <AlertCircle size={14} className="mt-0.5 shrink-0"/> {errorMsg}
                </div>
             )}
          </div>

          {/* Action Buttons */}
          <div className="p-5 border-t border-gray-100 flex flex-col gap-3 bg-white">
             <div className="flex gap-3">
                <button onClick={runChecks} className="flex-1 py-2.5 px-4 rounded-xl border border-gray-300 text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors flex items-center justify-center gap-2 text-sm">
                   <RefreshCw size={16}/> Check Again
                </button>
                <button 
                  onClick={handleStartExam} 
                  disabled={!canStart}
                  className={`flex-1 py-2.5 px-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 text-sm shadow-lg
                    ${canStart ? 'bg-gray-900 hover:bg-orange-600 shadow-orange-500/20' : 'bg-gray-300 cursor-not-allowed shadow-none'}`}
                >
                   Start Exam <PlayCircle size={16}/> 
                </button>
             </div>
             <button onClick={() => { cleanupMedia(); navigate("/dashboard?tab=my"); }} className="text-center text-xs text-gray-400 hover:text-gray-600 flex items-center justify-center gap-1 transition-colors">
                <ArrowLeft size={12}/> Return to Dashboard
             </button>
          </div>
        </section>

      </div>
    </div>
  );
}

export default ExamCheck;